/** Static volcanic geology and finite, source-connected lava volumes. Geometry,
 * surface queries and flow coordinates are deterministic in planet-local metres. */
import {hashSeed} from './rng.js';
import * as pmath from './pmath.js';
import {planarField} from './landforms.js';
import type {PlanetDef} from './universe.js';

type V3=[number,number,number];
export interface LavaPoint {x:number;y:number;s:number;heightM:number;half:number}
export interface VolcanoDef {
  origin:V3;
  across:V3;
  downhill:V3;
  radius:number;
  height:number;
  baseM:number;
  crater:number;
  poolRadius:number;
  poolM:number;
  seed:number;
  flow:readonly LavaPoint[];
}
export interface VolcanicSample {heightM:number;lavaM:number;lavaDepthM:number;weight:number;flowS:number;cross:number}
interface Province {def:VolcanoDef}
const DEPTH=.42;
const smooth=(v:number):number=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};
const norm=(x:number,y:number,z:number):V3=>{const k=1/Math.sqrt(x*x+y*y+z*z);return[x*k,y*k,z*k];};

export function volcanoDirection(v:VolcanoDef,radius:number,x:number,y:number):V3 {
  return norm(v.origin[0]*radius+v.across[0]*x+v.downhill[0]*y,
    v.origin[1]*radius+v.across[1]*x+v.downhill[1]*y,
    v.origin[2]*radius+v.across[2]*x+v.downhill[2]*y);
}

/** Native cone and shield, excluding the incised vent outlet and lava channel. */
function rock(v:VolcanoDef,x:number,y:number,nativeM:number):number {
  const radial=Math.sqrt(x*x*.94+y*y*1.06),r=v.radius;
  const envelope=1-smooth((radial/r-1.40)/.72);
  if(envelope<=0)return nativeM;
  // Radial buttresses break up the silhouette, rather than circular noise rings.
  const angle=pmath.atan2(y,x),phase=(v.seed>>>0)/4294967296*pmath.TWO_PI;
  const ribs=pmath.cos(angle*7+phase)*.075+pmath.cos(angle*3-phase)*.09;
  const flankR=radial*(1+ribs*smooth((radial-v.crater)/(r*.35)));
  const flank=Math.max(0,1-(flankR-v.crater)/(r-v.crater));
  const bowl=.80+.20*smooth(radial/v.crater);
  const cone=(radial<v.crater?bowl:flank)*v.height;
  // Broad planar facets and radial shoulders; the lip stays uneven but finite.
  const facet=planarField(x/(r*.25),y/(r*.25),2.7,v.seed);
  const rough=(radial<v.crater?smooth(radial/v.crater):Math.min(1,flank*2)) * facet*v.height*.20;
  const apron=planarField(x/145,y/145,5.1,v.seed)*62
    +planarField(x/390,y/390,1.7,v.seed)*90;
  const height=cone+rough+apron*(1-smooth(flank*2));
  // Old basalt beds form broad benches with short faces on the lower apron.
  const offset=planarField(x/220,y/220,9.1,v.seed)*48;
  const layer=(height+offset)/58,whole=Math.floor(layer),fraction=layer-whole;
  const shelf=(whole+smooth((fraction-.52)/.38))*58-offset;
  const shaped=v.baseM+height+(shelf-height)*.70*(1-smooth(flank*6));
  return nativeM+(shaped-nativeM)*envelope;
}

export class VolcanicTerrain {
  readonly features:readonly VolcanoDef[];
  private readonly provinces:Province[]=[];
  constructor(private readonly planet:PlanetDef,native:(x:number,y:number,z:number)=>number) {
    if(planet.archetype!=='volcanic'||planet.terrain.amplitudeRel<.001){this.features=[];return;}
    const R=planet.radius,r=Math.min(2100,R*.082),sea=(planet.seaRadius??-1e9)-R;
    const phase=(hashSeed(`${planet.seed}:volcanoes:v1`)>>>0)/4294967296*pmath.TWO_PI;
    const candidates:{v:VolcanoDef;score:number}[]=[];
    for(let i=0;i<384;i++) {
      const y=1-2*(i+.5)/384,a=i*2.399963229728653+phase,q=Math.sqrt(1-y*y);
      const origin:V3=[q*pmath.cos(a),y,q*pmath.sin(a)],h=native(...origin);
      if(h<sea+70)continue;
      const east=norm(origin[2],0,-origin[0]);
      const north:V3=[origin[1]*east[2],origin[2]*east[0]-origin[0]*east[2],-origin[1]*east[0]];
      const at=(e:number,n:number)=>native(...norm(origin[0]+(east[0]*e+north[0]*n)/R,
        origin[1]+north[1]*n/R,origin[2]+(east[2]*e+north[2]*n)/R));
      const ex=at(r*.7,0),wx=at(-r*.7,0),ny=at(0,r*.7),sy=at(0,-r*.7);
      let gx=wx-ex,gy=sy-ny;if(Math.abs(gx)+Math.abs(gy)<.1){gx=1;gy=0;}
      const downhill=norm(east[0]*gx+north[0]*gy,north[1]*gy,east[2]*gx+north[2]*gy);
      const across:V3=[downhill[1]*origin[2]-downhill[2]*origin[1],downhill[2]*origin[0]-downhill[0]*origin[2],downhill[0]*origin[1]-downhill[1]*origin[0]];
      const seed=hashSeed(`${planet.seed}:volcano:${i}`),variety=(seed>>>0)/4294967296;
      const featureR=r*(.86+.18*variety);
      const baseM=(h+ex+wx+ny+sy)/5,height=featureR*(.59+.08*variety),crater=featureR*.135;
      if(baseM<sea+90)continue;
      const v:VolcanoDef={origin,across,downhill,radius:featureR,height,baseM,crater,poolRadius:crater*.60,
        poolM:baseM+height*.80,seed,flow:[]};
      const variation=Math.max(ex,wx,ny,sy)-Math.min(ex,wx,ny,sy);
      candidates.push({v,score:variation+Math.abs(h-baseM)*2+Math.abs(h-160)*.15});
    }
    candidates.sort((a,b)=>a.score-b.score);
    for(const {v} of candidates) {
      if(this.provinces.some(p=>{
        const x=p.def.origin[0]-v.origin[0],y=p.def.origin[1]-v.origin[1],z=p.def.origin[2]-v.origin[2];
        return Math.sqrt(x*x+y*y+z*z)*R<r*4.6;
      }))continue;
      const flow:LavaPoint[]=[];
      let previous=v.poolM,arc=0,lastX=0,lastY=0;
      const step=12,count=Math.floor(v.radius*1.72/step),phase=(v.seed>>>0)/4294967296*pmath.TWO_PI;
      // An exact node at the pool outlet prevents interpolated downhill height
      // from starting inside the level pool and jumping at its circular rim.
      const distances=Array.from({length:count+1},(_,j)=>j*step);
      if(!distances.includes(v.poolRadius))distances.push(v.poolRadius);
      distances.sort((a,b)=>a-b);
      for(let j=0;j<distances.length;j++) {
        const y=distances[j];
        const bend=smooth((y-v.crater)/(v.radius*.45));
        const x=((pmath.sin(y/(v.radius*.21)+phase)-pmath.sin(phase))*.055
          +pmath.sin(y/(v.radius*.08))*.012)*v.radius*bend;
        const nativeM=native(...volcanoDirection(v,R,x,y)),ground=rock(v,x,y,nativeM);
        const ds=j?Math.sqrt((x-lastX)*(x-lastX)+(y-lastY)*(y-lastY)):0;arc+=ds;
        // A real descending surface, including the crater's notched outlet.
        // Stop on an uphill obstruction beyond the cone instead of tunnelling.
        const heightM=y<=v.poolRadius?v.poolM:Math.min(previous-ds*.009,ground+.08);
        if(j>0&&(heightM<sea+6||(y>v.radius*1.15&&ground-heightM>8)))break;
        const half=(8+6*smooth(y/v.radius))*Math.min(1,v.radius/700)*(1+.16*pmath.sin(y/91+phase));
        flow.push({x,y,s:arc,heightM,half});previous=heightM;lastX=x;lastY=y;
      }
      if(flow.length<12)continue;
      // A cooled terminal lobe closes smoothly to zero, rather than a square end.
      for(let i=Math.max(0,flow.length-6);i<flow.length;i++)flow[i].half*=smooth((flow.length-1-i)/5);
      v.flow=flow;
      this.provinces.push({def:v});if(this.provinces.length===(planet.parentId?1:3))break;
    }
    this.features=this.provinces.map(p=>p.def);
  }

  sample(dx:number,dy:number,dz:number,nativeM:number,out:VolcanicSample):VolcanicSample {
    out.heightM=nativeM;out.lavaM=-1e9;out.lavaDepthM=0;out.weight=0;out.flowS=0;out.cross=0;
    const R=this.planet.radius;
    for(const {def:v} of this.provinces) {
      const dot=dx*v.origin[0]+dy*v.origin[1]+dz*v.origin[2];if(dot<.96)continue;
      const x=(dx*v.across[0]+dy*v.across[1]+dz*v.across[2])*R/dot;
      const y=(dx*v.downhill[0]+dy*v.downhill[1]+dz*v.downhill[2])*R/dot;
      const radial=Math.sqrt(x*x*.94+y*y*1.06),weight=1-smooth((radial/v.radius-1.4)/.72);
      if(weight<=0)continue;
      let bed=rock(v,x,y,nativeM),lava=-1e9,flowS=0,cross=0;
      const poolDist=Math.sqrt(x*x+y*y),poolDepth=DEPTH*(1-smooth((poolDist/v.poolRadius-.8)/.2));
      let wetDepth=poolDepth;
      const poolBlend=1-smooth((poolDist-v.poolRadius)/(v.crater-v.poolRadius));
      bed+=(v.poolM-poolDepth-bed)*poolBlend;
      if(poolDepth>0)lava=v.poolM;
      // y is strictly increasing. A single continuous chart coordinate avoids
      // discontinuous heights on the medial axes of nearest-segment queries.
      let lo=0,hi=v.flow.length-1;
      while(hi-lo>1){const mid=(lo+hi)>>1;if(v.flow[mid].y<=y)lo=mid;else hi=mid;}
      {
        const a=v.flow[lo],b=v.flow[hi],t=Math.max(0,Math.min(1,(y-a.y)/(b.y-a.y)));
        const cx=a.x+(b.x-a.x)*t,cy=a.y+(b.y-a.y)*t;
        const dist=Math.sqrt((x-cx)*(x-cx)+(y-cy)*(y-cy));
        const half=a.half+(b.half-a.half)*t,ws=a.heightM+(b.heightM-a.heightM)*t;
        const bank=half+Math.max(10,Math.max(0,bed-ws)*.52);
        if(dist<bank&&half>.01) {
          const depth=DEPTH*(1-smooth(dist/half));
          wetDepth=Math.max(wetDepth,depth);
          const k=(1-smooth((dist-half)/Math.max(1,bank-half)))*smooth(half/2);
          bed+=(ws-depth-bed)*k;
          if(depth>0&&ws>lava){lava=ws;flowS=a.s+(b.s-a.s)*t;cross=dist/half;}
        }
      }
      // At a tiny cooling tip the carving fades into native rock. A low native
      // facet must not leave a deep sliver of liquid under the disappearing rim.
      if(lava>-1e8)lava=Math.min(lava,bed+wetDepth);
      out.heightM=bed;out.lavaM=lava;out.lavaDepthM=lava>-1e8?Math.max(0,lava-bed):0;
      out.weight=weight;out.flowS=flowS;out.cross=cross;return out;
    }
    return out;
  }
}
